import React, { useContext, useState } from 'react';

import styled from 'styled-components';

import { isBookingEvent } from '@libs/EventTypes';
import { getMoment } from '@libs/NwMoment';
import { showTime, isAllDay } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { useAddModelToDate } from '@hooks/API/Event/UseEvent';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import AcknowledgementEvent from '@components/EventButtons/AcknowledgementEvent';
import CancelEvent from '@components/EventButtons/CancelEvent';
import EventTypeTag from '@components/EventDetails/EventTypeTag';
import { NwButton, NwButtonGroup } from '@components/Gui/NwButton';
import NwEllipsis from '@components/Gui/NwEllipsis';
import ProjectLine from '@components/ProjectLine/ProjectLine';
import MobileAgendaEventTime from '@components/Schedule/Agenda/MobileAgendaEventTime';

import { useAgendaContext } from './AgendaContext';
import { EventModelName } from './EventModelName';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const AgendaEventContainer = styled.div`
    background-color: white;
    margin: 0.5rem 0;
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    min-height: 3rem;
    ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}

    .agenda-event-content {
        min-height: 3rem;
        opacity: ${(props) => props.isAvailable && 0.5};
        width: 100%;

        .agenda-event-name {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 0.25rem;
            padding: 0.25rem 0;

            .ant-tag {
                ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}
            }
            .netwalk-event-tag {
                margin-top: 0.25rem;
                margin-bottom: 0.15rem;
            }

            .agenda-event-nametext {
                font-weight: bold;
                font-size: 0.8rem;
                line-height: ${(props) => props.theme.FontSizeSm};
                text-transform: uppercase;
                ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}
            }

            &.agenda-event-name-stacked {
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 0.15rem;
            }
        }

        .agenda-event-description {
            padding-right: 2rem;
            line-height: 1.1em;
        }

        .agenda-event-models-list {
            padding: 0.5rem 0;
            text-transform: capitalize;
            em {
                font-style: normal;
            }
        }

        .agenda-event-models-exceptions {
            padding: 0.15rem 0;
            text-transform: capitalize;
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 1rem;
            .time-left {
                min-width: 8rem;
                strong {
                    display: block;
                    text-transform: uppercase;
                }
            }
            .models-right {
            }
        }

        .models-inline-list {
            display: flex;
            flex-wrap: wrap;
            gap: 0 0.5rem;
        }
        .models-stacked-list {
            display: flex;
            flex-direction: column;
            gap: 0;
        }
    }
`;

const AgendaEventToolbar = styled.div`
    padding-top: ${(props) => (props.$mobileView ? '.75rem' : '1.25rem')};
    padding-right: 0.5rem;
    opacity: ${(props) => props.isAvailable && 0.5};
`;

const ButtonGroupWrapper = styled.div`
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-right: 1px solid #d9d9d9 !important;
        &.event-cancelled-button {
            border-right: 1px solid ${(props) => props.theme.DangerColor} !important;
        }
    }
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }
`;

const MobileAgendaEvent = ({ event }) => {
    const { contactId, loadMonthEvs, onSelectEvent, previewMode, scheduleRange, selectedEvent, type } =
        useAgendaContext();

    const isAvailable = event.isAvailable;
    //const date = getMoment(scheduleRange.start);
    const { mutateAsync: onAddModelDate } = useAddModelToDate();

    // Utility function to sort models with cancelled ones at the end
    const sortModelsWithCancelledLast = (models) => {
        return models.sort((a, b) => {
            const aCancelled = a.Cancel ? 1 : 0;
            const bCancelled = b.Cancel ? 1 : 0;
            return aCancelled - bCancelled;
        });
    };

    const onReload = () => {
        loadMonthEvs();
    };

    const onAddModelEvent = async (event) => {
        try {
            await onAddModelDate({
                eventDateId: event.EventDateID,
                modelId: contactId,
            });
            onReload();
        } catch (error) {
            console.error('Add Model Event Error => ', error);
        }
    };

    const { cachedList } = useContext(ListContext);
    const [loading, setLoading] = useState(false);
    const breakpoints = useNwBreakPoints();

    let cls = '';
    if (event === selectedEvent) {
        cls = 'agenda-event-selected';
    }

    const eventtype = cachedList.eventTypes.items.find((et) => et.code === event.TypeName);

    const hasAcknowledgement = () => {
        if (
            eventtype.code !== 'ToDo' &&
            eventtype.code !== 'Meeting' &&
            eventtype.code !== 'ExtJob' &&
            eventtype.code !== 'Accommodation' &&
            eventtype.code !== 'Notice'
        ) {
            return true;
        }
        return false;
    };
    const hasCancel = () => {
        if (
            eventtype.code !== 'ToDo' &&
            eventtype.code !== 'Meeting' &&
            eventtype.code !== 'Accommodation' &&
            eventtype.code !== 'Notice'
        ) {
            return true;
        }
        return false;
    };

    const handleAddModelEvent = async () => {
        setLoading(true);
        await onAddModelEvent(event);
        setLoading(false);
    };

    const renderModelsAndExceptions = () => {
        if (!isBookingEvent(event.TypeName)) {
            return event.ModelID ? (
                <div className="agenda-event-models-list">
                    <EventModelName modelID={event.ModelID} event={event} model={event} />
                </div>
            ) : (
                ''
            );
        } else {
            const exceptions = event.ModelsDates.filter((modelDate) => modelDate.isException);
            if (exceptions.length === 0) {
                let modelList = [];
                for (const modelDate of event.ModelsDates) {
                    for (const modelofdate of modelDate.Models) {
                        if (!modelList.find((item) => item.ModelID === modelofdate.ModelID)) {
                            modelList.push(modelofdate);
                        }
                    }
                }
                const sortedModelList = sortModelsWithCancelledLast(modelList);
                const listClassName = 'models-inline-list'; // sortedModelList.length > 10 ? 'models-inline-list' : 'models-stacked-list';

                return (
                    <div className={`agenda-event-models-list ${listClassName}`}>
                        {sortedModelList.map((model) => (
                            <EventModelName key={model.ModelID} modelID={model.ModelID} model={model} event={event} />
                        ))}
                    </div>
                );
            } else {
                //check max number of models in all exceptions
                // const maxModels = event.ModelsDates.reduce((max, exception) => {
                //     return Math.max(max, exception.Models.length);
                // }, 0);
                const listClassName = 'models-inline-list'; //maxModels > 5 ? 'models-inline-list' : 'models-stacked-list';

                return event.ModelsDates.map((exception, index) => (
                    <div key={index} className="agenda-event-models-exceptions">
                        <div className="time-left">
                            <strong>
                                {isAllDay(exception.FromDate, exception.ToDate)
                                    ? 'all day'
                                    : `${showTime(exception.FromDate)} > ${showTime(exception.ToDate)}`}
                            </strong>
                            {exception.CallTime && <strong>Call Time: {exception.CallTime}</strong>}
                        </div>
                        <div className={`models-right ${listClassName}`}>
                            {sortModelsWithCancelledLast(exception.Models).map((model) => (
                                <EventModelName
                                    key={model.ModelID}
                                    modelID={model.ModelID}
                                    model={model}
                                    event={event}
                                />
                            ))}
                        </div>
                    </div>
                ));
            }
        }
    };

    const renderAgendaToolbar = () => {
        if (type !== 'all' && event.TypeName !== 'Accommodation') {
            return (
                <AgendaEventToolbar $mobileView={breakpoints === 1}>
                    {isAvailable ? (
                        <NwButtonGroup size="small">
                            <NwButton
                                icon={byPrefixAndName.fasl['plus']}
                                loading={loading}
                                onClick={(e) => {
                                    handleAddModelEvent();
                                    e.stopPropagation();
                                }}
                            />
                        </NwButtonGroup>
                    ) : (
                        <>
                            {(hasAcknowledgement() || hasCancel()) && (
                                <ButtonGroupWrapper>
                                    <NwButtonGroup size="small">
                                        {hasAcknowledgement() && (
                                            <AcknowledgementEvent
                                                button
                                                previewMode={previewMode}
                                                onStatusChanged={onReload}
                                                acknowledgmentValue={event.Acknowledgment}
                                                type={event.TypeName}
                                                eventId={event.ID}
                                                eventDateModelID={event.EventDateModelID}
                                            />
                                        )}
                                        {hasCancel() && (
                                            <CancelEvent
                                                button
                                                eventData={event}
                                                previewMode={previewMode}
                                                onStatusChanged={onReload}
                                                cancelValue={event.Cancel}
                                                type={event.TypeName}
                                                eventId={event.ID}
                                                modelID={event.ModelID}
                                                eventDateModelID={event.EventDateModelID}
                                            />
                                        )}
                                    </NwButtonGroup>
                                </ButtonGroupWrapper>
                            )}
                        </>
                    )}
                </AgendaEventToolbar>
            );
        } else {
            return <></>;
        }
    };

    return (
        <AgendaEventContainer
            cancel={event.Cancel}
            isAvailable={isAvailable}
            className={cls}
            onClick={() => !previewMode && onSelectEvent && onSelectEvent(event)}
        >
            <MobileAgendaEventTime
                event={event}
                eventType={eventtype}
                isAvailable={isAvailable}
                currentDate={scheduleRange.start}
            />
            <div className="agenda-event-content">
                {event.Project && <ProjectLine small project={event.Project} ellipsis mg="0" />}
                <div className={`agenda-event-name ${event.Name?.length > 30 ? 'agenda-event-name-stacked' : ''}`}>
                    <EventTypeTag event={event} />
                    <div className="agenda-event-nametext">{event.Name}</div>
                </div>
                {eventtype.id !== 150 && (
                    <div className="agenda-event-description">
                        <NwEllipsis noFontSize text={event.Description} rows={2} />
                    </div>
                )}
                {type === 'all' && <div className="agenda-event-models">{renderModelsAndExceptions()}</div>}
                {breakpoints === 1 && renderAgendaToolbar()}
            </div>
            {breakpoints !== 1 && renderAgendaToolbar()}
        </AgendaEventContainer>
    );
};

export default MobileAgendaEvent;
